import { IconName } from 'components/Icons';

const MIME_TO_ICON: Array<{ file: (mimeType: string) => boolean; icon: IconName }> = [
  // PDF
  { file: (m) => m === 'application/pdf', icon: 'filePdf' },

  // Obrazy
  { file: (m) => m.startsWith('image/'), icon: 'fileImage' },

  // Wideo
  { file: (m) => m.startsWith('video/'), icon: 'fileVideo' },

  // Audio
  { file: (m) => m.startsWith('audio/'), icon: 'fileAudio' },

  // Dokumenty (Word, Excel, PowerPoint, itp.)
  { file: (m) => m.startsWith('application/vnd') || m === 'application/msword', icon: 'fileDoc' },

  // Archiwa
  { file: (m) => ['application/zip', 'application/x-rar-compressed', 'application/x-7z-compressed'].includes(m), icon: 'fileArchive' },

  // Tekst i inne
  { file: (m) => m.startsWith('text/'), icon: 'file' },
];

const DEFAULT_ICON: IconName = 'file';

/**
 * Zwraca nazwę ikony odpowiadającą danemu MIME type.
 */
export const getIconFromMimeType = (mimeType: string | null | undefined): IconName => {
  if (!mimeType) return DEFAULT_ICON;

  return MIME_TO_ICON.find(({ file }) => file(mimeType))?.icon ?? DEFAULT_ICON;
};

export type MimeCategory =
  | 'image'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'text'
  | 'document'
  | 'archive'
  | 'unknown';

const ARCHIVE_MIME_TYPES = [
  'application/zip',
  'application/x-rar-compressed',
  'application/x-7z-compressed',
];

const DOCUMENT_MIME_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

/**
 * Zwraca kategorię MIME type używaną do wyboru renderera podglądu.
 *
 * @example
 * getMimeCategory('image/jpeg')          // → 'image'
 * getMimeCategory('application/pdf')      // → 'pdf'
 * getMimeCategory('video/mp4')            // → 'video'
 * getMimeCategory('audio/mpeg')           // → 'audio'
 * getMimeCategory('text/plain')           // → 'text'
 * getMimeCategory('application/zip')      // → 'archive'
 * getMimeCategory('application/msword')   // → 'document'
 * getMimeCategory(null)                   // → 'unknown'
 */
export const getMimeCategory = (mimeType: string | null | undefined): MimeCategory => {
  if (!mimeType) return 'unknown';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('text/')) return 'text';
  if (ARCHIVE_MIME_TYPES.includes(mimeType)) return 'archive';
  if (DOCUMENT_MIME_TYPES.includes(mimeType) || mimeType.startsWith('application/vnd')) return 'document';
  return 'unknown';
};
